"use client"

import {
  useCallback,
  useEffect,
  useState,
} from "react"

type SnapPoint = number

type UsePlayerDrawerReturn = {
  open: boolean
  snap: SnapPoint

  setSnap: (value: SnapPoint) => void

  openDrawer: () => void
  closeDrawer: () => void
  toggleDrawer: () => void

  setOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >
}

const STORAGE_KEY =
  "mixtape-player-drawer"

const DEFAULT_SNAP = 0.62

export default function usePlayerDrawer(): UsePlayerDrawerReturn {
  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    snap,
    setSnapState,
  ] = useState<SnapPoint>(
    DEFAULT_SNAP
  )

  /* =========================================================
     RESTORE
  ========================================================= */

  useEffect(() => {
    if (
      typeof window === "undefined"
    ) {
      return
    }

    const stored =
      localStorage.getItem(
        STORAGE_KEY
      )

    if (!stored) return

    try {
      const parsed =
        JSON.parse(stored)

      if (
        typeof parsed.open ===
        "boolean"
      ) {
        setOpen(parsed.open)
      }

      if (
        typeof parsed.snap ===
        "number" &&
        Number.isFinite(parsed.snap)
      ) {
        setSnapState(parsed.snap)
      }
    } catch {
      localStorage.removeItem(
        STORAGE_KEY
      )
    }
  }, [])

  /* =========================================================
     PERSIST
  ========================================================= */

  useEffect(() => {
    if (
      typeof window === "undefined"
    ) {
      return
    }

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        open,
        snap,
      })
    )
  }, [
    open,
    snap,
  ])

  /* =========================================================
     ACTIONS
  ========================================================= */

  const openDrawer =
    useCallback(() => {
      setOpen(true)
    }, [])

  const closeDrawer =
    useCallback(() => {
      setOpen(false)
    }, [])

  const toggleDrawer =
    useCallback(() => {
      setOpen(
        (prev) => !prev
      )
    }, [])

  const setSnap =
    useCallback(
      (value: number) => {
        if (
          !Number.isFinite(value)
        ) {
          return
        }

        setSnapState(value)
      },
      []
    )

  /* =========================================================
     ESC
  ========================================================= */

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (
      event: KeyboardEvent
    ) => {
      if (
        event.key === "Escape"
      ) {
        setOpen(false)
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown
    )

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      )
    }
  }, [open])

  /* =========================================================
     BODY LOCK
  ========================================================= */

  useEffect(() => {
    if (!open) {
      document.body.style.removeProperty(
        "overflow"
      )

      document.body.style.removeProperty(
        "touch-action"
      )

      return
    }

    document.body.style.overflow =
      "hidden"

    document.body.style.touchAction =
      "none"

    return () => {
      document.body.style.removeProperty(
        "overflow"
      )

      document.body.style.removeProperty(
        "touch-action"
      )
    }
  }, [open])

  /* =========================================================
     RETURN
  ========================================================= */

  return {
    open,
    snap,

    setSnap,

    openDrawer,
    closeDrawer,
    toggleDrawer,

    setOpen,
  }
}